import { DefaultValue, selector, selectorFamily } from 'recoil';
import { DeviceType } from '../enums/Device.enum';
import {
  deviceTypeState,
  allProductsState,
  _sizingToolState,
  _modalState,
  _cartDrawerState,
  _cartState,
  _selectionKlaviyoResponseFamily,
  activeNavCategorySetState,
  environmentContextState,
  _bundleCollectionSwatchAtom,
  _selectionSizeFamily,
  _selectionFitFamily,
  _selectionConfirmedFamily,
  _selectionColorFamily,
} from './atoms';
import { Common, Selections } from './keys';
import { ViewProduct } from '../models/ViewProduct.model';
import { ProductHandle } from '../enums/ProductHandle.enum';
import { getHashVariable, scrollToTop, setURLParam, uniqueArray } from '../util';
import { trackSizingToolCompleted, trackSizingToolStarted, trackSizingToolStyleSelected } from '../tracking/events';
import { ModalToggleState, SizingToolState } from '../models/actionStates';
import { ModalState } from '../models/ModalState';
import { DrawerState } from '../models/DrawerState';
import { guardRecoilDefaultValue } from './helpers/recoilHelpers';
import { ShopifyCart } from '../models/ShopifyCart.model';
import { SizingToolStateProps } from '../models/SizingToolState.model';
import { ReceiveKlaviyoResponsePayload } from '../models/klaviyo/SubscribeToKlaviyo';
import { NavCategory } from '../enums/NavCategory.enum';
import { NavCategorySet } from '../models/NavCategorySet.model';
import { BundleCollectionHandle } from '../enums/CollectionHandle.enum';
import { bundleConfiguration } from '../hooks/bundleConfiguration';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { LongSize } from '../product_detail/components/size.enum';
import { SelectionType } from '../enums/SelectionType.enum';

export const isMobileState = selector({
  key: Common.IsMobile,
  get: ({ get }) => {
    const deviceType = get(deviceTypeState);
    return deviceType === DeviceType.Mobile;
  },
});

export const isTabletState = selector({
  key: Common.IsTablet,
  get: ({ get }) => {
    const deviceType = get(deviceTypeState);
    return deviceType === DeviceType.Tablet;
  },
});

export const isDesktopState = selector({
  key: Common.IsDesktop,
  get: ({ get }) => {
    const deviceType = get(deviceTypeState);
    return deviceType === DeviceType.Desktop;
  },
});

export const productsState = selector({
  key: Common.Products,
  get: ({ get }) => {
    const allProducts = get(allProductsState);
    return allProducts;
  },
});

export const productListState = selector({
  key: Common.ProductList,
  get: ({ get }) => {
    const allProducts = get(allProductsState);
    const products = (Object.keys(allProducts) as ProductHandle[]).map((handle: ProductHandle) => allProducts[handle]);
    return products;
  },
});

// This filters down the products to the selected collections category
// If the Discounted category is selected, it will also filter out non-discounted variants
export const categoryFilterProductsState = selector({
  key: Common.CategoryFilterProducts,
  get: ({ get }) => {
    const navCategorySet = get(activeNavCategorySetState);
    const { warehouseSaleSKUs } = get(environmentContextState);
    const productList = get(productListState);
    return getAvailableProducts(navCategorySet, warehouseSaleSKUs, productList);
  },
});

export const getAvailableProducts = (navCategorySet: NavCategorySet, warehouseSaleSKUs: string[], productList: ViewProduct[]): ViewProduct[] => {
  const filterCategories = navCategorySet?.productTags ?? [];
  const navCategory = navCategorySet?.navCategory || NavCategory.All;
  const isDiscountedProducts = navCategory === NavCategory.Discounted;
  const isAllProducts = navCategory === NavCategory.All;
  const discountedVariantFilter = (product: ViewProduct) => product.variants.filter((variant) => warehouseSaleSKUs.includes(variant.sku));
  const discountFilter = (product: ViewProduct): boolean => discountedVariantFilter(product).length > 0;

  // returns a copy of discounted products with only the discounted colors
  const filterDiscountedVariantsFn =
    (discountActive: boolean) =>
    (product: ViewProduct): ViewProduct => {
      if (!discountActive) {
        return product;
      }
      const variants = discountedVariantFilter(product);
      const uniqueColors = uniqueArray(variants.map(({ color }) => color));
      return { ...product, variants, colors: uniqueColors };
    };

  const filterProducts = (p: ViewProduct): boolean => {
    const isVisible = !p.hiddenInMenus;
    const tagMatches = p.tags.some((c) => filterCategories.includes(c));
    const discountMatches = isDiscountedProducts && discountFilter(p);
    const hasColors = !!p.colors.length; // for discounted items
    return isVisible && hasColors && (isAllProducts || tagMatches || discountMatches);
  };
  const ret = productList.map(filterDiscountedVariantsFn(isDiscountedProducts)).filter(filterProducts);
  // console.log({ ret });
  return ret;
};

export const selectionKlaviyoResponseFamily = selectorFamily<ReceiveKlaviyoResponsePayload | undefined, string>({
  key: Selections.KlaviyoResponse,
  get:
    (formId: string) =>
    ({ get }) => {
      return get(_selectionKlaviyoResponseFamily(formId));
    },
  set:
    (formId: string) =>
    ({ set }, response: ReceiveKlaviyoResponsePayload | undefined | DefaultValue) => {
      if (!guardRecoilDefaultValue(response)) {
        set(_selectionKlaviyoResponseFamily(formId), response);
      }
    },
});

export interface BundleProductState {
  productHandle: ProductHandle;
  color: Color | undefined;
  size: LongSize | undefined;
  fit: Fit | undefined;
  quantity: number;
  confirmedForStepper: boolean;
}

export interface BundleCollectionState {
  selectedSwatch: string;
  products: BundleProductState[];
}

export const bundleSelectionKey = (collectionHandle: BundleCollectionHandle, productHandle: ProductHandle, index: number) =>
  `${collectionHandle}_${productHandle}_${index}`;

export const bundleCollectionState = selectorFamily<BundleCollectionState, BundleCollectionHandle>({
  key: Selections.BundleCollection,
  get:
    (collectionHandle) =>
    ({ get }) => {
      const config = bundleConfiguration[collectionHandle];
      const selectedSwatch = get(_bundleCollectionSwatchAtom(collectionHandle));
      const productHandles = config?.productColorCombos.find((pcc) => pcc.swatchTitle === selectedSwatch)!.colorCombos.map((cc) => cc.productHandle);
      const products = productHandles.map((productHandle, i) => {
        const key = bundleSelectionKey(collectionHandle, productHandle, i);
        const productState: BundleProductState = {
          productHandle,
          color: config.productColorCombos.find((pcc) => pcc.swatchTitle === selectedSwatch)!.colorCombos[i].color,
          size: get(_selectionSizeFamily({ selectionType: SelectionType.BundleProductSection, key })),
          fit: get(_selectionFitFamily({ selectionType: SelectionType.BundleProductSection, key })),
          quantity: config.productColorCombos.find((pcc) => pcc.swatchTitle === selectedSwatch)!.colorCombos[i].quantity,
          confirmedForStepper: get(_selectionConfirmedFamily({ selectionType: SelectionType.BundleProductSection, key })),
        };
        return productState;
      });

      const currentState: BundleCollectionState = {
        selectedSwatch,
        products,
      };

      return currentState;
    },
  set:
    (collectionHandle) =>
    ({ set, get }, state: BundleCollectionState | DefaultValue) => {
      if (guardRecoilDefaultValue(state)) {
        set(_bundleCollectionSwatchAtom(collectionHandle), state);
      } else {
        // TODO: Analytics here
        setURLParam('bundleColor', state.selectedSwatch);
        set(_bundleCollectionSwatchAtom(collectionHandle), state.selectedSwatch);

        const config = bundleConfiguration[collectionHandle];
        const selectedSwatch = state.selectedSwatch;
        const productHandles = config?.productColorCombos.find((pcc) => pcc.swatchTitle === selectedSwatch)!.colorCombos.map((cc) => cc.productHandle);
        productHandles.forEach((productHandle, i) => {
          const key = bundleSelectionKey(collectionHandle, productHandle, i);
          const existingSize = get(_selectionSizeFamily({ selectionType: SelectionType.BundleProductSection, key }));
          const existingFit = get(_selectionFitFamily({ selectionType: SelectionType.BundleProductSection, key }));
          const existingColor = get(_selectionColorFamily({ selectionType: SelectionType.BundleProductSection, key }));
          const existingConfirmedForStepper = get(_selectionConfirmedFamily({ selectionType: SelectionType.BundleProductSection, key }));

          if (existingSize !== state.products[i].size) {
            set(_selectionSizeFamily({ selectionType: SelectionType.BundleProductSection, key }), state.products[i].size);
          }

          if (existingFit !== state.products[i].fit) {
            set(_selectionFitFamily({ selectionType: SelectionType.BundleProductSection, key }), state.products[i].fit);
          }

          if (existingConfirmedForStepper !== state.products[i].confirmedForStepper) {
            set(_selectionConfirmedFamily({ selectionType: SelectionType.BundleProductSection, key }), state.products[i].confirmedForStepper);
          }

          if (existingColor !== state.products[i].color) {
            set(_selectionColorFamily({ selectionType: SelectionType.BundleProductSection, key }), state.products[i].color);
          }
        });
      }
    },
});

export const sizingToolState = selector<SizingToolStateProps>({
  key: Selections.SizingToolState,
  get: ({ get }) => {
    return get(_sizingToolState);
  },
  set: ({ set, get }, state: SizingToolStateProps | DefaultValue) => {
    if (guardRecoilDefaultValue(state)) {
      set(_sizingToolState, state);
    } else {
      const previousState = get<SizingToolStateProps>(_sizingToolState);
      const mergedState = { ...previousState, ...state };

      if (!mergedState.skipTracking && mergedState.sizingToolState === SizingToolState.Open) {
        trackSizingToolStarted();
      }
      if (mergedState.sizingToolState === SizingToolState.Complete) {
        trackSizingToolCompleted();
      }

      if (state.product?.handle && previousState.product?.handle !== state.product?.handle) {
        trackSizingToolStyleSelected(state.product);
      }

      set(_sizingToolState, mergedState);
    }
  },
});

export const modalState = selector<ModalState>({
  key: Selections.ModalState,
  get: ({ get }) => {
    return get(_modalState);
  },
  set: ({ set }, state: ModalState | DefaultValue) => {
    if (!guardRecoilDefaultValue(state)) {
      if (state?.toggleState === ModalToggleState.Open) {
        if (!state.isReverse && state.isBaseState !== false) {
          const openModalIndex = getHashVariable('open-modal');
          const newIndex = openModalIndex && +openModalIndex + 1;
          history.pushState({}, '', `#open-modal=${openModalIndex ? newIndex : 1}`);
        } else if (state.isReverse && !state.fromPopState) {
          // if from pop state, index is already reversed by the back button click
          const openModalIndex = getHashVariable('open-modal');
          const newIndex = openModalIndex && +openModalIndex ? +openModalIndex - 1 : 0;
          if (newIndex) {
            history.pushState({}, '', `#open-modal=${newIndex}`);
          } else {
            const uri = window.location.toString();
            if (uri.indexOf('#') > 0) {
              const clean_uri = uri.substring(0, uri.indexOf('#'));
              history.replaceState({}, document.title, clean_uri);
            }
          }
        }

        state.scrollToTop && scrollToTop();
        document.body.classList.add('modal-open');
      } else {
        const uri = window.location.toString();
        if (uri.indexOf('#') > 0) {
          // const openModalIndex = getHashVariable('open-modal');
          // if (openModalIndex) {
          //   for (let i = 0; i < +openModalIndex; i++) {
          //     history.back();
          //   }
          // }
          const clean_uri = uri.substring(0, uri.indexOf('#'));
          history.replaceState({}, document.title, clean_uri);
        }
        document.body.classList.remove('modal-open');
      }
      set(_modalState, cleanPreviousState(state));
    }
  },
});
const cleanPreviousState = (state: ModalState) => (state.toggleState === ModalToggleState.Closed ? { ...state, previousState: undefined } : state);

export const cartDrawerState = selector<DrawerState>({
  key: Selections.CartDrawer,
  get: ({ get }) => {
    return get(_cartDrawerState);
  },
  set: ({ set }, state: DrawerState | DefaultValue) => {
    set(_cartDrawerState, state);
  },
});

export const cartState = selector<ShopifyCart>({
  key: Selections.Cart,
  get: ({ get }) => {
    return get(_cartState);
  },
  set: ({ set }, state: ShopifyCart | DefaultValue) => {
    set(_cartState, state);
  },
});
